import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api, { API } from '@/lib/api';
import { track as trackEvent } from '@/lib/analytics';
import { SoundscapeMixer } from '@/lib/soundscape';
import { resolveNatureUrl as resolveNatureUrlShared } from '@/lib/natureTracks';

const PlayerContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MEDITATION_KINDS = new Set(['meditation', 'ai_meditation', 'ai_visualization']);

function resolveAudioUrl(url) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
}

// Convert a saved background_sound shape to the layer list the mixer wants.
// Colour and nature volumes mirror the user's current bg slider so the
// mixer's master gain is the single volume knob we need to ride.
function toLayers(bg) {
    if (!bg) return [];
    const layers = [];
    if (bg.color) layers.push({ kind: 'color', key: bg.color, volume: 0.9, sleep: !!bg.sleep });
    if (bg.nature) layers.push({ kind: 'nature', key: bg.nature, volume: 0.9, sleep: false });
    return layers;
}

export function PlayerProvider({ children }) {
    const audioRef = useRef(null);
    if (!audioRef.current && typeof window !== 'undefined') {
        const a = new Audio();
        a.preload = 'metadata';
        audioRef.current = a;
    }
    const [track, setTrack] = useState(null); // { id, title, subtitle, image, audio_url, kind, duration_minutes }
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // seconds
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.85);
    const [speed, setSpeed] = useState(1.0);
    const [fullscreen, setFullscreen] = useState(false);
    const [timerMinutes, setTimerMinutes] = useState(null);
    const timerEndsAt = useRef(null);
    const sessionLoggedRef = useRef(false);
    const startedAtRef = useRef(null);
    const moodBeforeRef = useRef(null);
    // Pre-mood gate (set when a meditation-kind track is queued; player won't
    // start audio until pre-mood is resolved via confirmPreMood).
    const [preMoodGate, setPreMoodGate] = useState(false);
    // Post-mood pending — set after a session is successfully logged. The
    // PostMoodToast component reads + clears this.
    const [postMoodSession, setPostMoodSession] = useState(null);

    // Background soundscape attached to the current voice session.
    // `backgroundSound` is the saved spec; `bgVolume` is the user slider
    // (0..1). Effective bg volume = bgVolume * (playing ? duck : 1).
    const [backgroundSound, setBackgroundSound] = useState(null);
    const [bgVolume, setBgVolume] = useState(0.4);
    const [bgEnabled, setBgEnabled] = useState(true);
    const bgMixerRef = useRef(null);
    const musicCatalogRef = useRef([]);

    // Fetch the music catalog once so we can resolve nature keys → URLs.
    useEffect(() => {
        api.get('/music').then((r) => { musicCatalogRef.current = r.data || []; }).catch(() => {});
    }, []);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => setProgress(a.currentTime || 0);
        const onMeta = () => setDuration(a.duration || 0);
        const onEnd = async () => {
            setPlaying(false);
            await maybeLogSession(true);
        };
        a.addEventListener('timeupdate', onTime);
        a.addEventListener('loadedmetadata', onMeta);
        a.addEventListener('ended', onEnd);
        return () => {
            a.removeEventListener('timeupdate', onTime);
            a.removeEventListener('loadedmetadata', onMeta);
            a.removeEventListener('ended', onEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = speed;
    }, [speed]);

    // Background sound lifecycle: start when a bg spec is set + playback
    // begins; stop when the current track changes or the user disables it.
    const _ensureBgMixer = useCallback(() => {
        if (!bgMixerRef.current) {
            const resolveNatureUrl = (key) => resolveNatureUrlShared(musicCatalogRef.current, key);
            bgMixerRef.current = new SoundscapeMixer(resolveNatureUrl);
        }
        return bgMixerRef.current;
    }, []);

    const _stopBg = useCallback(() => {
        bgMixerRef.current?.stop({ keepContext: true });
    }, []);

    // Accept an explicit bg spec to sidestep the stale-closure problem that
    // afflicted the previous overrideBackgroundSound: setBackgroundSound +
    // _startBg in the same tick would read the OLD state. Callers now pass
    // the fresh spec directly.
    const _startBg = useCallback(async (bgOverride, volumeOverride) => {
        if (!bgEnabled) return;
        const bg = bgOverride !== undefined ? bgOverride : backgroundSound;
        const layers = toLayers(bg);
        if (layers.length === 0) return;
        const m = _ensureBgMixer();
        m.setMasterBase(volumeOverride !== undefined ? volumeOverride : bgVolume);
        // Begin ducked under voice if playback is in flight; the play/pause
        // handlers will keep this in sync afterwards.
        m.setDuckRatio(playing ? 0.5 : 1.0);
        await m.play(layers);
    }, [bgEnabled, backgroundSound, bgVolume, playing, _ensureBgMixer]);

    // Keep master base in sync with the user slider.
    useEffect(() => {
        bgMixerRef.current?.setMasterBase(bgVolume);
    }, [bgVolume]);

    // Toggle enable/disable: stop immediately when disabled; restart when
    // re-enabled during an active voice session.
    useEffect(() => {
        if (!bgEnabled) {
            _stopBg();
            return;
        }
        if (playing && backgroundSound) {
            _startBg(backgroundSound);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgEnabled]);

    // Sleep timer
    useEffect(() => {
        if (!timerMinutes) { timerEndsAt.current = null; return; }
        timerEndsAt.current = Date.now() + timerMinutes * 60 * 1000;
        const t = setInterval(() => {
            if (timerEndsAt.current && Date.now() >= timerEndsAt.current) {
                pause();
                setTimerMinutes(null);
                clearInterval(t);
            }
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timerMinutes]);

    const maybeLogSession = useCallback(async (completed = false) => {
        if (sessionLoggedRef.current) return;
        if (!track || !startedAtRef.current) return;
        const elapsedSec = (Date.now() - startedAtRef.current) / 1000;
        const elapsedMin = elapsedSec / 60;
        // Log only if at least 30 seconds were listened to (or finished)
        if (!completed && elapsedSec < 30) return;
        const minutes = Math.min(Math.max(elapsedMin, 0.5), 120);
        try {
            const { data } = await api.post('/sessions', {
                kind: track.kind || 'meditation',
                item_id: track.id,
                duration_minutes: Number(minutes.toFixed(1)),
                mood_before: moodBeforeRef.current || track.mood_before || null,
                mood_after: null,
            });
            sessionLoggedRef.current = true;
            try { trackEvent('session-logged', { kind: track?.kind || 'meditation', minutes: Number(minutes.toFixed(1)), completed: !!completed }); } catch {}
            // Only ask for a post-mood for meditation-flavoured kinds, not
            // ambient music. Fully optional — the toast lets the user dismiss.
            if (MEDITATION_KINDS.has(track.kind || 'meditation')) {
                setPostMoodSession({
                    session_id: data?.id || null,
                    track_id: track.id,
                    track_title: track.title,
                    kind: track.kind || 'meditation',
                    completed,
                });
            }
        } catch (e) {
            // non-fatal
        }
    }, [track]);

    const _startPlayback = useCallback((t) => {
        const a = audioRef.current;
        if (!a) return;
        sessionLoggedRef.current = false;
        startedAtRef.current = Date.now();
        setTrack(t);
        a.src = resolveAudioUrl(t.audio_url);
        a.loop = !!t.loop;
        a.currentTime = 0;
        a.play().then(() => {
            setPlaying(true);
            // Start the attached background soundscape once voice is rolling.
            if (backgroundSound) _startBg();
        }).catch(() => setPlaying(false));
    }, [backgroundSound, _startBg]);

    const pendingTrackRef = useRef(null);

    const playTrack = useCallback((t) => {
        const a = audioRef.current;
        if (!a) return;
        // log previous if needed
        if (track && track.id !== t.id) {
            maybeLogSession(false);
        }
        // Stop any previous background so the new track's bg (or lack thereof)
        // takes effect cleanly.
        _stopBg();
        // Pick up the saved background sound attached to this track (AI
        // meditation metadata). The caller can override by setting
        // backgroundSound explicitly before calling playTrack.
        const savedBg = t?.metadata?.background_sound || t?.background_sound || null;
        setBackgroundSound(savedBg || null);
        // Restore the saved mix volume too so the slider reflects what the
        // user originally dialed in. Falls back to 0.4 when no saved volume.
        if (savedBg && typeof savedBg.volume === 'number') {
            setBgVolume(Math.max(0, Math.min(1, savedBg.volume)));
        }
        setBgEnabled(true);
        // For meditation-flavoured kinds, open the pre-mood gate first; the
        // FullScreenPlayer renders the inline picker and calls confirmPreMood
        // (with a mood or null=Skip) to actually start audio. Ambient music
        // and unknown kinds bypass the gate.
        moodBeforeRef.current = null;
        if (MEDITATION_KINDS.has(t.kind || 'meditation')) {
            pendingTrackRef.current = t;
            // Surface the track in the UI without starting audio yet.
            setTrack(t);
            setPlaying(false);
            setPreMoodGate(true);
            return;
        }
        _startPlayback(t);
    }, [track, maybeLogSession, _startPlayback, _stopBg]);

    const confirmPreMood = useCallback(async (mood) => {
        const t = pendingTrackRef.current;
        pendingTrackRef.current = null;
        setPreMoodGate(false);
        if (mood) {
            moodBeforeRef.current = mood;
            try {
                await api.post('/mood', {
                    mood,
                    context: 'pre',
                    track_id: t?.id || null,
                });
            } catch {}
        }
        if (t) _startPlayback(t);
    }, [_startPlayback]);

    const logPostMood = useCallback(async (mood) => {
        const s = postMoodSession;
        setPostMoodSession(null);
        if (!mood || !s) return;
        try {
            await api.post('/mood', {
                mood,
                context: 'post',
                session_id: s.session_id,
                track_id: s.track_id,
            });
        } catch {}
    }, [postMoodSession]);

    const dismissPostMood = useCallback(() => setPostMoodSession(null), []);

    const play = useCallback(() => {
        const a = audioRef.current;
        if (!a || !track) return;
        if (!startedAtRef.current) startedAtRef.current = Date.now();
        a.play().then(() => {
            setPlaying(true);
            if (backgroundSound) {
                // Ensure bg is playing + ducked under voice.
                if (!bgMixerRef.current?.playing) _startBg();
                bgMixerRef.current?.setDuckRatio(0.5);
            }
        }).catch(() => {});
    }, [track, backgroundSound, _startBg]);

    const pause = useCallback(() => {
        const a = audioRef.current;
        if (!a) return;
        a.pause();
        setPlaying(false);
        // Lift the duck so the ambient breathes at full user level while paused.
        bgMixerRef.current?.setDuckRatio(1.0);
        maybeLogSession(false);
    }, [maybeLogSession]);

    const toggle = useCallback(() => { playing ? pause() : play(); }, [playing, play, pause]);

    const seek = useCallback((seconds) => {
        const a = audioRef.current;
        if (!a) return;
        a.currentTime = Math.max(0, Math.min(seconds, a.duration || seconds));
    }, []);

    const skip = useCallback((delta) => {
        const a = audioRef.current;
        if (!a) return;
        a.currentTime = Math.max(0, Math.min((a.currentTime || 0) + delta, a.duration || 0));
    }, []);

    const stop = useCallback(() => {
        const a = audioRef.current;
        if (!a) return;
        a.pause();
        setPlaying(false);
        setFullscreen(false);
        _stopBg();
        setBackgroundSound(null);
        maybeLogSession(false);
        setTrack(null);
    }, [maybeLogSession, _stopBg]);

    // Allow the fullscreen player (or AI meditation form) to override the
    // active background sound mid-session.
    const overrideBackgroundSound = useCallback((bg) => {
        setBackgroundSound(bg || null);
        _stopBg();
        if (bg && playing) {
            // Pass the fresh spec in — _startBg captures state at call time
            // so we can't rely on the just-set backgroundSound yet.
            _startBg(bg);
        }
    }, [playing, _stopBg, _startBg]);

    const value = useMemo(() => ({
        track, playing, progress, duration, volume, speed, fullscreen, timerMinutes,
        preMoodGate, postMoodSession,
        playTrack, play, pause, toggle, seek, skip, stop,
        confirmPreMood, logPostMood, dismissPostMood,
        setVolume, setSpeed, setFullscreen, setTimerMinutes,
        // Background soundscape attached to the current voice session
        backgroundSound, bgVolume, bgEnabled,
        setBgVolume, setBgEnabled, overrideBackgroundSound,
    }), [track, playing, progress, duration, volume, speed, fullscreen, timerMinutes, preMoodGate, postMoodSession, playTrack, play, pause, toggle, seek, skip, stop, confirmPreMood, logPostMood, dismissPostMood, backgroundSound, bgVolume, bgEnabled, overrideBackgroundSound]);

    return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
    const ctx = useContext(PlayerContext);
    if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
    return ctx;
}

export function formatTime(seconds) {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}
